import { useCallback, useEffect, useState } from 'react';
import { T } from '@design/index';
import { patientApi, type ConsentRequestRow } from '../api';

/**
 * Requests waiting on the patient — the half of consent the web app never had.
 *
 * A doctor or hospital asks for access through the platform, and until now the
 * patient's web app only listed the grants the patient started themselves.
 * The request sat unanswered with the requesting side waiting on it, and
 * nothing on this screen even showed that it existed.
 *
 * Allow and deny are both real, audited decisions (`POST
 * /consent-requests/:id/allow` and `/deny`). Nothing is assumed: a request
 * that fails to send stays pending and says so.
 */

function fmtWhen(v: string): string {
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '';
  const mins = Math.floor((Date.now() - d.getTime()) / 60000);
  if (mins < 60) return `${Math.max(mins, 1)} min ago`;
  if (mins < 1440) return `${Math.floor(mins / 60)} hr ago`;
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

export default function ConsentRequestsInbox({ onDecided }: { onDecided?: () => void }) {
  const [pending, setPending] = useState<ConsentRequestRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const o = await patientApi.consentOverview();
      setPending(o?.pending ?? []);
    } catch (e: any) {
      setError(e?.body?.message || 'Requests waiting on you could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const decide = async (row: ConsentRequestRow, allow: boolean) => {
    setBusyId(row.id);
    setError('');
    try {
      if (allow) await patientApi.allowConsentRequest(row.id);
      else await patientApi.denyConsentRequest(row.id);
      setPending((rows) => rows.filter((r) => r.id !== row.id));
      onDecided?.();
    } catch (e: any) {
      // The request is still pending — say so rather than removing the card.
      setError(e?.body?.message || 'That decision could not be recorded. The request is still waiting.');
    } finally {
      setBusyId('');
    }
  };

  if (loading) {
    return (
      <div style={{
        background: T.white, border: `1px solid ${T.border}`, borderRadius: 16,
        padding: 14, fontFamily: T.font, fontSize: 12, color: T.textSec, marginBottom: 14,
      }}>
        Checking for access requests…
      </div>
    );
  }

  // Nothing waiting is the normal state; an empty card would only add noise.
  if (!error && pending.length === 0) return null;

  return (
    <div style={{ marginBottom: 16 }}>
      {error && (
        <div style={{
          background: T.white, border: `1px solid ${T.danger}55`, borderRadius: 14,
          padding: 12, marginBottom: 10, fontFamily: T.font, fontSize: 12, color: T.danger,
        }}>
          {error}
        </div>
      )}

      {pending.length > 0 && (
        <>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, fontFamily: T.font,
          }}>
            <span style={{ fontSize: 13, fontWeight: 800, color: T.text }}>Waiting on you</span>
            <span style={{
              fontSize: 10, fontWeight: 700, color: T.white, background: T.warning,
              borderRadius: 10, padding: '2px 8px',
            }}>
              {pending.length}
            </span>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {pending.map((r) => (
              <div
                key={r.id}
                style={{
                  background: T.white, border: `1px solid ${T.warning}55`, borderRadius: 16,
                  padding: 16, fontFamily: T.font, boxShadow: T.shadow,
                }}
              >
                <div style={{ fontSize: 13.5, fontWeight: 800, color: T.text }}>{r.requester}</div>
                <div style={{ fontSize: 11, color: T.textSec, marginTop: 2 }}>
                  {[r.requesterType, r.requesterNhcId].filter(Boolean).join(' · ')}
                </div>

                {r.purpose && (
                  <div style={{ fontSize: 12, color: T.text, marginTop: 10, lineHeight: 1.5 }}>
                    {r.purpose}
                  </div>
                )}

                {r.records.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
                    {r.records.map((rec) => (
                      <span
                        key={rec}
                        style={{
                          fontSize: 10, fontWeight: 600, color: T.teal,
                          background: `${T.teal}12`, border: `1px solid ${T.teal}30`,
                          borderRadius: 8, padding: '3px 8px',
                        }}
                      >
                        {rec}
                      </span>
                    ))}
                  </div>
                )}

                <div style={{ fontSize: 10.5, color: T.textTer, marginTop: 10 }}>
                  {[r.duration ? `Access for ${r.duration}` : null, `Asked ${fmtWhen(r.requestedAt)}`]
                    .filter(Boolean)
                    .join(' · ')}
                </div>

                <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
                  <button
                    onClick={() => decide(r, true)}
                    disabled={busyId === r.id}
                    style={{
                      flex: 1, padding: '10px 0', borderRadius: 10, border: 'none',
                      cursor: busyId === r.id ? 'default' : 'pointer',
                      fontSize: 12, fontWeight: 700, fontFamily: T.font,
                      color: T.white, background: T.teal, opacity: busyId === r.id ? 0.6 : 1,
                    }}
                  >
                    {busyId === r.id ? 'Recording…' : 'Allow access'}
                  </button>
                  <button
                    onClick={() => decide(r, false)}
                    disabled={busyId === r.id}
                    style={{
                      flex: 1, padding: '10px 0', borderRadius: 10,
                      border: `1px solid ${T.danger}40`, background: T.white,
                      cursor: busyId === r.id ? 'default' : 'pointer',
                      fontSize: 12, fontWeight: 700, fontFamily: T.font, color: T.danger,
                      opacity: busyId === r.id ? 0.6 : 1,
                    }}
                  >
                    Decline
                  </button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
